import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const num = (v) => Number(v) || 0;
const priceLabel = (p) => (p.value ? `£${num(p.value).toFixed(2)}${p.vat_rate ? ` + ${p.vat_rate}% VAT` : ""}` : "no price yet");
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Turn a saved extra into a sale-agreement line; VAT is stored as a rate and converted to pounds.
export const extraFromPreset = (p) => ({
  description: p.description, value: p.value ? num(p.value).toFixed(2) : "", vat: p.value && p.vat_rate ? ((num(p.value) * p.vat_rate) / 100).toFixed(2) : "",
});

// Save a sale line as a usual extra, updating the saved one with the same description if it exists.
export const saveAsUsual = async (e, presets) => {
  const description = e.description.trim();
  if (!description) throw new Error("Add a description first");
  const vat_rate = num(e.value) > 0 ? Math.round((num(e.vat) / num(e.value)) * 10000) / 100 : 0;
  if (vat_rate > 30) throw new Error("VAT looks higher than 30% of the value, please check it");
  const body = { description, value: num(e.value), vat_rate };
  const match = presets.find((p) => p.description.toLowerCase() === description.toLowerCase());
  const { data } = match ? await api.put(`/admin/extra-presets/${match.id}`, body) : await api.post("/admin/extra-presets", body);
  return match ? presets.map((p) => (p.id === match.id ? data : p)) : [...presets, data];
};

export function useExtraPresets() {
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    api.get("/admin/extra-presets").then((r) => setPresets(Array.isArray(r.data) ? r.data : [])).catch(() => setPresets([]));
  }, []);
  return [presets, setPresets];
}

export function UsualExtras({ presets, setPresets, onPick }) {
  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.description}" from your usual extras?`)) return;
    try {
      await api.delete(`/admin/extra-presets/${p.id}`);
      setPresets(presets.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <div className="mb-4" data-testid="usual-extras">
      <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Your usual extras, tap to add</p>
      {presets.length === 0 && <p className="text-sm text-charcoal/75" data-testid="usual-extras-empty">None saved yet. Use "Save as usual extra" on any line below.</p>}
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <span key={p.id} className="inline-flex items-center border border-stone bg-paper text-sm" data-testid={`usual-extra-${slug(p.description)}`}>
            <button type="button" onClick={() => onPick(p)} className="inline-flex items-center gap-1.5 px-3 py-2 hover:bg-stone-light" data-testid={`usual-extra-${slug(p.description)}-add`}>
              <Plus size={14} /> {p.description} <span className="text-charcoal/75">· {priceLabel(p)}</span>
            </button>
            <button type="button" onClick={() => remove(p)} aria-label={`Remove ${p.description} from usual extras`} className="border-l border-stone px-2 py-2 text-red-800 hover:bg-red-50" data-testid={`usual-extra-${slug(p.description)}-remove`}><X size={14} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}
